import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getAllGraphNodes, getGraphNode, getGraphNeighbors, graphNodeKey, isGraphUnavailable } from 'DataGraph/dataGraphApi';
import { layoutNetwork } from './layoutNetwork';
import NetworkDiagram from './NetworkDiagram';
import NetworkLegend from './NetworkLegend';
import { formatRecords, formatStorage, edgeKindText } from './format';
import { ObjectChoiceBar } from './objectChoice';
import './SalesforceNetwork.css';

const HOP_OPTIONS = [1, 2, 3];
// "Referenced by" opens by itself only when short — a hub object (Account, User) has hundreds of
// referencing fields, which would push the field list off the page.
const REFERENCED_BY_OPEN_MAX = 10;

/** Each field's outgoing lookups, from the neighbours response: field name (lower-cased) → [{ table, kind }]. */
export function lookupsByField(direct) {
  const out = new Map();
  (direct || []).filter((d) => d.role === 'PARENT').forEach((d) => {
    const key = String(d.via || '').toLowerCase();
    if (!out.has(key)) out.set(key, []);
    const list = out.get(key);
    if (!list.some((t) => t.table === d.node.table)) list.push({ table: d.node.table, kind: d.edge && d.edge.kind });
  });
  out.forEach((list) => list.sort((a, b) => String(a.table).localeCompare(String(b.table))));
  return out;
}

/** Fields filtered by name or type, in the Describe's own order. */
export function filterFields(columns, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return columns || [];
  return (columns || []).filter((c) => [c.name, c.dataType].some((s) => s && String(s).toLowerCase().includes(q)));
}

/**
 * One Salesforce object: the network of what it links to and what links to it (1–3 hops) on top,
 * its fields underneath. The body only — the profile page and the Data Graph each wrap it in
 * their own header and route, and both pass `onOpenObject(table)` to move to another object.
 *
 * `choice` (a useObjectChoice result, Data Graph only) adds the chosen-objects bar with an Add /
 * Remove button for this object, and the map dims the objects outside the choice. Without it (the
 * profile page) there is no bar and nothing is dimmed.
 *
 * The node list is loaded once per source and kept while the user walks from object to object
 * (the route's component stays mounted when only `objectName` changes).
 */
// `filterSlot(node)` (Data Graph only) renders the object's row-filter editor under the header; the
// profile page passes none.
const SalesforceObjectDetail = ({ dataSourceId, graphId, objectName, nouns, choice = null, onOpenObject, filterSlot = null }) => {
  const picked = choice ? choice.picked : null;
  const [nodes, setNodes] = useState(null);
  const [detail, setDetail] = useState(null);
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);
  const [hops, setHops] = useState(1);
  const [includeManual, setIncludeManual] = useState(true);
  const [neighbors, setNeighbors] = useState(null);
  const [neighborsLoading, setNeighborsLoading] = useState(false);
  const [fieldQuery, setFieldQuery] = useState('');
  const [referencedByOpen, setReferencedByOpen] = useState(null);   // null = by length
  const neighborCache = useRef(new Map());
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  // Every node, for sizing the map's circles. A failure here only costs the sizes.
  useEffect(() => {
    let cancelled = false;
    neighborCache.current = new Map();
    getAllGraphNodes(dataSourceId, { graphId })
      .then((res) => { if (!cancelled) setNodes(res.items || []); })
      .catch(() => { if (!cancelled) setNodes([]); });
    return () => { cancelled = true; };
  }, [dataSourceId, graphId]);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    setDetail(null);
    setFieldQuery('');
    setReferencedByOpen(null);
    getGraphNode(dataSourceId, objectName, undefined, graphId)
      .then((res) => { if (!cancelled) { setDetail(res); setState('ready'); setError(null); } })
      .catch((e) => {
        if (cancelled) return;
        if (isGraphUnavailable(e)) setState('missing');
        else { setState('error'); setError((e && e.message) || 'Could not load this object.'); }
      });
    return () => { cancelled = true; };
  }, [dataSourceId, graphId, objectName]);

  useEffect(() => {
    if (state !== 'ready') { setNeighbors(null); return undefined; }
    const cacheKey = `${objectName}|${hops}|${includeManual}`;
    const cached = neighborCache.current.get(cacheKey);
    if (cached) { setNeighbors(cached); return undefined; }
    let cancelled = false;
    setNeighborsLoading(true);
    getGraphNeighbors(dataSourceId, objectName, {
      hops, direction: 'both', includeManual, includeInferred: true, graphId,
    }).then((res) => {
      if (cancelled || !mounted.current) return;
      neighborCache.current.set(cacheKey, res);
      setNeighbors(res);
    }).catch(() => {
      if (cancelled || !mounted.current) return;
      setNeighbors({ direct: [], indirect: [], edges: [], failed: true });
    }).finally(() => { if (!cancelled && mounted.current) setNeighborsLoading(false); });
    return () => { cancelled = true; };
  }, [dataSourceId, graphId, objectName, hops, includeManual, state]);

  const nodesByKey = useMemo(() => new Map((nodes || []).map((n) => [graphNodeKey(n), n])), [nodes]);
  const center = useMemo(() => {
    if (!detail || !detail.node) return null;
    return nodesByKey.get(graphNodeKey(detail.node)) || detail.node;
  }, [detail, nodesByKey]);
  const layout = useMemo(() => (center && neighbors
    ? layoutNetwork({ center, direct: neighbors.direct, indirect: neighbors.indirect, edges: neighbors.edges, nodesByKey, hops })
    : null), [center, neighbors, nodesByKey, hops]);

  const direct = (neighbors && neighbors.direct) || [];
  const lookups = useMemo(() => lookupsByField(neighbors && neighbors.direct), [neighbors]);
  const referencedBy = direct.filter((d) => d.role === 'CHILD').slice()
    .sort((a, b) => String(a.node.table).localeCompare(String(b.node.table)) || String(a.via).localeCompare(String(b.via)));
  const columns = useMemo(() => (detail && detail.columns) || [], [detail]);
  const fields = useMemo(() => filterFields(columns, fieldQuery), [columns, fieldQuery]);
  const recordNoun = (nouns && nouns.record) || 'records';
  const open = (table) => { if (onOpenObject && table && table !== objectName) onOpenObject(table); };

  if (state === 'loading') return <div className='sfn-empty' data-testid='sf-object-detail-loading'>Loading {objectName}…</div>;
  if (state === 'missing') {
    return (
      <div className='sfn-empty' data-testid='sf-object-detail-missing'>
        {objectName} is not in this org&apos;s latest Describe, or the org has not been described yet.
      </div>
    );
  }
  if (state === 'error') return <div className='sfn-empty' data-testid='sf-object-detail-error'>{error}</div>;

  const attrs = (center && center.attributes) || {};
  const showReferencedBy = referencedByOpen == null ? referencedBy.length <= REFERENCED_BY_OPEN_MAX : referencedByOpen;

  return (
    <div className='sfn-object' data-testid='sf-object-detail'>
      <div className='sfn-object__head'>
        <h2 className='sfn-object__title sfn-mono'>{objectName}</h2>
        <div className='sfn-panel__meta'>
          {attrs.label && attrs.label !== objectName && <span>{attrs.label}</span>}
          <span>{formatRecords(center && center.rowCount)} {recordNoun}</span>
          <span>{formatStorage(center && center.sizeBytes)} est. storage</span>
          <span>{columns.length} fields</span>
          {attrs.keyPrefix && <span className='sfn-mono'>prefix {attrs.keyPrefix}</span>}
          {attrs.custom === true && <span className='sfn-tag sfn-tag--custom'>Custom</span>}
        </div>
      </div>

      {filterSlot && <div className='sfn-panel'>{filterSlot(center)}</div>}

      {choice && (
        <ObjectChoiceBar choice={choice} total={nodes ? nodes.length : null}
          itemNoun={(nouns && nouns.item) || 'objects'} current={objectName} />
      )}

      <div className='sfn-panel' data-testid='sf-object-network'>
        <div className='sfn-panel__head'>
          <div className='sfn-panel__title'>Links</div>
          <div className='sfn-controls'>
            <span>Hops</span>
            {HOP_OPTIONS.map((h) => (
              <button type='button' key={h} className={`sfn-chip${hops === h ? ' sfn-chip--on' : ''}`}
                data-testid={`sf-network-hops-${h}`} onClick={() => setHops(h)}>{h}</button>
            ))}
            <label>
              <input type='checkbox' checked={includeManual} onChange={(e) => setIncludeManual(e.target.checked)} />
              include manual links
            </label>
            {neighborsLoading && <span className='sfn-muted'>Loading links…</span>}
          </div>
        </div>
        {neighbors && neighbors.failed && <div className='sfn-empty'>Could not load this object’s links.</div>}
        {layout && layout.nodes.length > 1 && (
          <>
            <NetworkLegend layout={layout} />
            <NetworkDiagram layout={layout} onNodeClick={(n) => open(n.table)} picked={picked} />
          </>
        )}
        {layout && layout.nodes.length <= 1 && !neighborsLoading && !(neighbors && neighbors.failed) && (
          <div className='sfn-empty'>No links to or from this object in the Describe.</div>
        )}
        {referencedBy.length > 0 && (
          <div className='sfn-neighbours' data-testid='sf-network-table'>
            <button type='button' className='sfn-section__title sfn-toggle' data-testid='sf-referenced-by-toggle'
              aria-expanded={showReferencedBy} onClick={() => setReferencedByOpen(!showReferencedBy)}>
              {showReferencedBy ? '▾' : '▸'} Referenced by · {referencedBy.length}
            </button>
            {showReferencedBy && (
            <table>
              <thead>
                <tr><th>Object</th><th>Field</th><th>Link</th></tr>
              </thead>
              <tbody>
                {referencedBy.map((d) => (
                  <tr key={`${d.edge.id}`}>
                    <td>
                      <button type='button' className='sfn-link sfn-mono' onClick={() => open(d.node.table)}>{d.node.table}</button>
                    </td>
                    <td className='sfn-mono'>{`${d.node.table}.${d.via}`}</td>
                    <td>{edgeKindText(d.edge.kind)}{d.edge.polymorphicGroup ? ' · polymorphic' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>
        )}
      </div>

      <div className='sfn-panel sfn-fields' data-testid='sf-object-fields'>
        <div className='sfn-panel__head'>
          <div className='sfn-panel__title'>Fields · {columns.length}</div>
          <input type='text' className='sfn-search' data-testid='sf-field-search' placeholder='Search fields…'
            value={fieldQuery} onChange={(e) => setFieldQuery(e.target.value)} />
        </div>
        <table className='metadata-itbl'>
          <thead>
            <tr>
              <th>Field</th>
              <th>Type</th>
              <th>Key</th>
              <th>Required</th>
              <th>Looks up to</th>
            </tr>
          </thead>
          <tbody>
            {fields.length === 0 && (
              <tr><td colSpan={5} className='sfn-muted' style={{ textAlign: 'center', padding: 20 }}>
                {columns.length === 0 ? 'No fields recorded for this object.' : 'No fields match.'}
              </td></tr>
            )}
            {fields.map((c) => {
              const targets = lookups.get(String(c.name).toLowerCase()) || [];
              return (
                <tr key={c.name} data-testid={`sf-field-${c.name}`}>
                  <td className='sfn-mono'>{c.name}</td>
                  <td className='sfn-mono sfn-muted'>{c.dataType || '—'}</td>
                  <td>
                    {c.pk && <span className='sfn-tag'>PK</span>}
                    {!c.pk && c.unique && <span className='sfn-tag'>Unique</span>}
                  </td>
                  <td className='sfn-muted'>{c.nullable === false ? 'required' : ''}</td>
                  <td>
                    {targets.length === 0 && <span className='sfn-muted'>—</span>}
                    {targets.map((t, i) => (
                      <React.Fragment key={t.table}>
                        {i > 0 && ', '}
                        <button type='button' className='sfn-link sfn-mono' onClick={() => open(t.table)}
                          title={edgeKindText(t.kind)}>{t.table}</button>
                      </React.Fragment>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SalesforceObjectDetail;
